import { _ as _extends } from './_rollupPluginBabelHelpers-8c92bac3.js';
import React__default, { useState, useCallback, useEffect, useContext } from 'react';
import cx from 'classnames';
import { T as ThemeContext } from './ThemeContext-e1f446be.js';
import './FeedbackTooltip-fe34aaac.js';
import { a as isFunction, d as isBoolean, i as isArray, e as isDOMElement } from './helpers-1066b771.js';
import './inputIcon.js';
import { s as styles, a as Input } from './Input-5d5bcc53.js';
import PropTypes from 'prop-types';

const PrefixInput = props => {
  const [focused, setFocused] = useState(false);
  const {
    prefix,
    label,
    intent,
    inputProps,
    ...rest
  } = props;
  const {
    onFocus,
    onBlur
  } = inputProps;
  // we set focused class because border is set to parent container and focus input doesn't show correct border on input during focusing
  const focusHandler = useCallback(e => {
    const onEvent = focused ? onBlur : onFocus;
    if (isFunction(onEvent)) {
      onEvent(e);
    }
    setFocused(!focused);
  }, [focused, onBlur, onFocus]);
  return /*#__PURE__*/React__default.createElement("div", {
    className: cx('mw-container-prefix', `mw-input-intent-${intent}`, {
      'mw-container-prefix-focused': focused
    })
  }, label && /*#__PURE__*/React__default.createElement("div", {
    className: styles.labelPrefix
  }, label), /*#__PURE__*/React__default.createElement(Input, _extends({}, rest, {
    label: prefix,
    intent: intent,
    inputProps: {
      ...inputProps,
      onFocus: focusHandler,
      onBlur: focusHandler
    }
  })));
};

// ######### Default values for hooks ###########

const defaultKeyboardNavigationHandlers = {
  // for autocompletion :)
  onEscape: null,
  onArrowUp: null,
  onArrowDown: null,
  onArrowLeft: null,
  onArrowRight: null,
  onSpaceBar: null,
  onEnter: null
};
const defaultLockArrowsUsageKeys = ['ArrowUp', 'ArrowDown'];

// ######### Hooks definition ###################

// eslint-disable-next-line max-len
const useLockGlobalKeyboardNavigation = (dependency = false, keys = defaultLockArrowsUsageKeys) => {
  // when custom component use keyboard, we have to block window from usage them (to stop scrolling using keyboard)
  if (!isBoolean(dependency)) {
    // eslint-disable-next-line max-len
    console.error('useLockGlobalKeyboardNavigation hook can only toggle event listeners based on single boolean dependency. If your component need to lock arrows usage based on non-boolean value (eg. multiple state values) please pass the final result');
  }
  const lockArrowsUsage = e => keys.some(k => e.key === k) && e.preventDefault();
  const events = {
    add: () => window.addEventListener('keydown', lockArrowsUsage),
    remove: () => window.removeEventListener('keydown', lockArrowsUsage)
  };
  useEffect(() => {
    events.remove(); // make sure we have clear listener;

    if (dependency) {
      // add listener only when prop tells to do it
      events.add();
    }
    return events.remove;
  }, [dependency, events]);
};
const useKeyboard = (handlers = defaultKeyboardNavigationHandlers) => {
  const resolveKey = key => key === ' ' ? 'SpaceBar' : key; // https://www.w3.org/TR/uievents-key/#keys-whitespace

  const onKeyUp = e => {
    const eventHandler = handlers[`on${resolveKey(e.key)}`]; // https://reactjs.org/docs/events.html#keyboard-events

    if (isFunction(eventHandler)) {
      eventHandler(e);
    }
  };
  return [{
    onKeyUp
  }];
};

// eslint-disable-next-line max-len
const useFullState = initialState => {
  // https://reactjs.org/docs/hooks-reference.html#functional-updates <-- yellow note
  const [masterState, setMasterState] = useState({
    state: initialState,
    callback: null
  });
  const setState = useCallback((nextState, callback) => {
    setMasterState(({
      state: prevState
    }) => {
      return {
        callback,
        state: {
          ...prevState,
          ...(isFunction(nextState) ? nextState(prevState) : nextState)
        }
      };
    });
  }, []);
  useEffect(() => {
    if (isFunction(masterState.callback)) {
      masterState.callback(masterState.state);
    }
  }, [masterState]);
  return [masterState.state, setState];
};

const MultiSelect = props => {
  const $multiselect = /*#__PURE__*/React__default.createRef();
  const context = useContext(ThemeContext);
  const {
    value: valueFromProps
  } = props;
  const initialState = {
    cursor: -1,
    focus: false,
    options: props.options,
    searchValue: '',
    value: valueFromProps || []
  };
  const [state, setState] = useFullState(initialState);
  const {
    className,
    intent
  } = props;
  const {
    cursor,
    focus
  } = state;
  const isDark = props.theme || (context && context.theme) === 'dark';
  const multiSelectClassName = cx(className, 'mw-multiselect', {
    'focused': focus,
    'compact': !!props.compact,
    'intent-success': intent === 'success',
    'intent-error': intent === 'error'
  });
  const [keyboardHandlers] = useKeyboard({
    onEscape: e => onBlur(e),
    onArrowDown: () => {
      setState(({
        cursor,
        options
      }) => ({
        cursor: (cursor + 1) % options.length
      }));
    },
    onArrowUp: () => {
      setState(({
        cursor,
        options
      }) => ({
        cursor: (cursor - 1 + options.length) % options.length
      }));
    },
    onEnter: e => {
      const {
        options,
        cursor
      } = state;
      const element = options[cursor];
      if (element) {
        onSelect(options[cursor], e);
      }
    }
  });
  useLockGlobalKeyboardNavigation(focus);

  // Sync MultiSelect state with state of higher component.
  useEffect(() => {
    if (isArray(valueFromProps)) {
      setState({
        value: valueFromProps
      });
    } else {
      console.error('MultiSelect value has to be an array');
    }
  }, [valueFromProps, setState]);
  useEffect(() => {
    const currentListElement = document.querySelector('.multi-select-list .multi-select-active');
    if (isDOMElement(currentListElement)) {
      currentListElement.scrollIntoView({
        block: 'nearest',
        inline: 'start'
      });
    }
  }, [cursor, focus]);

  // Private methods:
  const onFocus = e => {
    const {
      inputProps
    } = props;
    const {
      onFocus
    } = inputProps;
    setState({
      focus: true
    }, nextState => {
      if (isFunction(onFocus)) {
        onFocus(nextState, e);
      }
    });
  };
  const onBlur = e => {
    e.target.blur();
    const {
      inputProps
    } = props;
    const {
      onBlur
    } = inputProps;
    const {
      value: _,
      ...freshState
    } = initialState; // eslint-disable-line no-unused-vars

    setState(freshState, nextState => {
      if (isFunction(onBlur)) {
        onBlur(nextState, e);
      }
      if ($multiselect.current && $multiselect.current.getBoundingClientRect().bottom < 0) {
        $multiselect.current.parentNode.parentNode.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    });
  };
  const onSelect = (selected, e) => {
    e.preventDefault();
    if (selected.disabled) return;
    const {
      inputProps
    } = props;
    const {
      onSelect
    } = inputProps;
    setState(({
      value
    }) => {
      if (value.find(v => v.value === selected.value)) {
        return {
          value: value.filter(v => v.value !== selected.value)
        };
      }
      return {
        value: [...value, selected]
      };
    }, nextState => {
      if (isFunction(onSelect)) {
        onSelect(selected, nextState);
      }
      onChange(null, nextState);
    });
  };
  const onChange = (e, nextState) => {
    const {
      onChange: onChangeHandler
    } = props;
    if (e) {
      const searchValue = e.target.value.replace(/ +/g, ' ');
      setState({
        searchValue,
        options: searchValue ? props.options.filter(c => c.text.toLowerCase().includes(searchValue.trim().toLowerCase())) : props.options
      }, nextState => {
        if (isFunction(onChangeHandler)) {
          onChangeHandler(nextState, e);
        }
      });
    } else {
      if (isFunction(onChangeHandler)) {
        onChangeHandler(nextState || state, e);
      }
    }
  };
  const filterInputProps = props => {
    const {
      onFocus,
      onBlur,
      onChange,
      onSelect,
      ...restInputProps
    } = props; // eslint-disable-line no-unused-vars

    return restInputProps;
  };
  const renderDropdown = () => {
    const {
      searchValue,
      options,
      cursor
    } = state;
    return /*#__PURE__*/React__default.createElement("ul", {
      className: cx('multi-select-list', {
        'multi-select-list-dark': isDark
      })
    }, options.length ? options.map((c, i) => {
      const checked = !!state.value.find(v => v.value === c.value);
      return /*#__PURE__*/React__default.createElement("li", {
        key: c.value,
        onMouseDown: e => onSelect(c, e),
        className: cx({
          'multi-select-active': i === cursor,
          'multi-select-disabled-option': c.disabled
        })
      }, /*#__PURE__*/React__default.createElement("div", {
        className: "multi-select-list-element"
      }, /*#__PURE__*/React__default.createElement("label", {
        htmlFor: c.value,
        className: "mw-checkbox"
      }, /*#__PURE__*/React__default.createElement("span", null, c.text), /*#__PURE__*/React__default.createElement("input", {
        id: c.value,
        type: "checkbox",
        checked: checked,
        readOnly: true
      }))));
    }) : /*#__PURE__*/React__default.createElement("li", {
      className: "multi-select-none"
    }, /*#__PURE__*/React__default.createElement("div", {
      className: "multi-select-list-element"
    }, /*#__PURE__*/React__default.createElement("label", {
      htmlFor: "none",
      className: "mw-checkbox"
    }, /*#__PURE__*/React__default.createElement("span", {
      className: "multi-select-no-result-text"
    }, "No results for \"", searchValue, "\"")))));
  };
  const renderControl = () => {
    const {
      id,
      placeholder,
      label,
      inputProps,
      inputNativeProps,
      searchBar,
      intent,
      feedbackTooltip = {}
    } = props;
    const {
      value,
      searchValue,
      focus
    } = state;
    const restInputProps = filterInputProps(inputProps);
    const nextValue = value.length === props.options.length ? `All (${value.length})` : value.map(v => v.text).join(', ');
    const inputValue = searchBar && focus ? searchValue : nextValue;
    const inputPlaceholder = !value.length ? placeholder : nextValue;
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
      className: "mw-title-title6"
    }, label), /*#__PURE__*/React__default.createElement("label", null, /*#__PURE__*/React__default.createElement(Input, _extends({
      type: "select",
      id: id,
      className: cx('mw-input-multiselect', {
        'mw-input-multiselect-dark': isDark
      }),
      inputProps: {
        placeholder: inputPlaceholder,
        value: inputValue,
        readOnly: searchBar ? false : 'readonly',
        onFocus: onFocus,
        onBlur: onBlur,
        onChange: onChange,
        autoComplete: 'off',
        /* eslint-disable-line max-len */ // e.g. chrome autocomplete remembers your old results eg. "Audi, Ford" and when you type "a" it will suggest you "Audi, Ford" which will be wrong as there is not such car as "Audi, Ford" + multiselect doesn't parse what you type.
        ...restInputProps,
        ...keyboardHandlers
      }
    }, inputNativeProps, {
      intent: intent,
      feedbackTooltip: feedbackTooltip
    }))));
  };
  return /*#__PURE__*/React__default.createElement("div", {
    ref: $multiselect,
    className: multiSelectClassName
  }, renderControl(), focus && renderDropdown());
};
MultiSelect.propTypes = {
  inputProps: PropTypes.object,
  inputNativeProps: PropTypes.object,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.array,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  searchBar: PropTypes.bool
};
MultiSelect.defaultProps = {
  inputProps: {},
  inputNativeProps: {},
  searchBar: false
};

export { MultiSelect as M, PrefixInput as P };
